import PropTypes from "prop-types";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  loadBenefitPlans,
  loadPendingClaims,
  fetchPendingClaimsReport,
  deleteClaim,
  loadClaimItems,
  getEmployeesByDelegation,
} from "actions/claimActions";
import {
  claimCategories,
  getClaimListByType,
  getAllBenefitPlans,
  getDefaultBenefitPlan,
  groupItemsByCategory,
  getAllEmployeeByDelegation,
  getDefaultDelegateEmployeeId,
} from "reducers/claimsReducer";
import { Card, CardSection, Toolbar, ToolbarSection, Button, Loader } from "cxa-components";
import { FileDownloadIcon } from "cxa-icons";
import { intlShape, injectIntl } from "react-intl";
import messages from "components/messagesDescription";
import {
  PendingClaimsTable,
  DeleteClaimDialog,
  PlanYearSelectField,
  DelegatorSelectField,
} from "./widgets";
import { compose, withHandlers, withState } from "recompose";
import { formValueSelector } from "redux-form";
import { Link } from "react-router-dom";
import ClaimItemSelectionForm from "./ClaimItemSelectionForm";
import { getShortLivedToken } from "actions/loginActions";
import { showEstimatedDateMessage } from "./util";

const benefitPeriodKey = "cxa_PendingBenefitPeriod";
const formName = "pendingClaimForm";

const mapDispatchToProps = {
  loadPendingClaims,
  fetchPendingClaimsReport,
  deleteClaim,
  loadBenefitPlans,
  loadClaimItems,
  getShortLivedToken,
  getEmployeesByDelegation,
};

const mapStateToProps = (
  {
    claims: {
      claimList,
      deleteClaim,
      downloadPendingClaimsUrl,
      benefitPlans,
      claimItems,
      claimItemGroups,
      claimItemCategories,
      employeesByDelegation: employeesDelegation,
      disclaimer,
    },
    ...state
  },
  { push }
) => {
  const savedBenefitPeriod = localStorage.getItem(benefitPeriodKey);
  const selector = formValueSelector(formName);
  const claims = getClaimListByType(claimList, "pending");
  const hasPendingClaims = claimCategories.some(
    category => claims && claims[category.id] && claims[category.id].length > 0
  );

  const defaultPlanId = getDefaultBenefitPlan(benefitPlans) || null;
  const benefitPeriod = selector(state, "benefitPeriod") || savedBenefitPeriod || defaultPlanId;
  const employeesByDelegation = getAllEmployeeByDelegation(employeesDelegation);
  const isEmployeesByDelegationEnabled = employeesByDelegation.length > 0;

  const defaultDelegateEmployeeId = getDefaultDelegateEmployeeId(employeesDelegation) || null;
  const delegateToEmployeeId =
    selector(state, "employeesByDelegation") || defaultDelegateEmployeeId;

  return {
    benefitPeriod,
    benefitPlans: getAllBenefitPlans(benefitPlans),
    claimCategories,
    claims,
    pageFields: claimList.pendingPageFields,
    hasPendingClaims,
    isFetching: claimItems.isFetching,
    isCPFEnabled: claimItems.isCPFEnabled,
    deleteClaimStatus: deleteClaim.status,
    downloadPendingClaimsUrl,
    initialValues: {
      benefitPeriod: savedBenefitPeriod || defaultPlanId,
    },
    isServiceProviderClaimItem: true,
    claimItems: groupItemsByCategory(claimItemGroups, claimItemCategories, claimItems),
    onSubmitSelection: ({ benefitPeriod, claimTypeId }) =>
      push(`/claims/submit/${claimTypeId}/${benefitPeriod}`),
    employeesByDelegation,
    isEmployeesByDelegationEnabled,
    delegateToEmployeeId,
    disclaimer,
  };
};

const enhancePendingClaims = compose(
  injectIntl,
  connect(mapStateToProps, mapDispatchToProps),
  withState("isDeleteItemModalOpen", "toggleDeleteItemModal", false),
  withState("modalClaimObjectId", "setModalClaimObjectId", null),
  withState("modalClaimId", "setModalClaimId", null),
  withHandlers({
    openDeleteItemModal: props => () => props.toggleDeleteItemModal(true),
    closeDeleteItemModal: props => () => props.toggleDeleteItemModal(false),
  })
);

class PendingClaimsPage extends Component {
  state = {
    isFetching: true,
  };

  componentDidMount() {
    const {
      loadBenefitPlans,
      loadClaimItems,
      loadPendingClaims,
      benefitPeriod,
      isServiceProviderClaimItem,
      getEmployeesByDelegation,
      delegateToEmployeeId,
      getDonateCategory,
    } = this.props;

    getEmployeesByDelegation().then(() => {
      loadBenefitPlans();

      if (benefitPeriod) {
        loadPendingClaims(benefitPeriod, delegateToEmployeeId, getDonateCategory);
        loadClaimItems(
          benefitPeriod,
          isServiceProviderClaimItem,
          delegateToEmployeeId,
          getDonateCategory
        );
      }
    });
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    const {
      benefitPeriod,
      isServiceProviderClaimItem,
      setModalClaimObjectId,
      setModalClaimId,
      toggleDeleteItemModal,
      deleteClaimStatus,
      loadPendingClaims,
      loadClaimItems,
      delegateToEmployeeId = null,
      getDonateCategory,
    } = nextProps;

    const isClaimOrDelegationChange =
      this.props.benefitPeriod !== benefitPeriod ||
      this.props.delegateToEmployeeId !== delegateToEmployeeId;

    if (!nextProps.isFetching && this.props.isFetching) {
      setModalClaimObjectId(null);
      setModalClaimId(null);
      toggleDeleteItemModal(false);
    }

    if (deleteClaimStatus) {
      loadPendingClaims(benefitPeriod, delegateToEmployeeId, getDonateCategory);
    }

    if (isClaimOrDelegationChange && benefitPeriod !== null) {
      loadPendingClaims(benefitPeriod, delegateToEmployeeId, getDonateCategory);
      loadClaimItems(
        benefitPeriod,
        isServiceProviderClaimItem,
        delegateToEmployeeId,
        getDonateCategory
      );
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.isFetching && !this.props.isFetching) {
      this.setState({
        isFetching: false,
      });
    }
  }

  componentWillUnmount() {
    const { benefitPeriod } = this.props;

    if (benefitPeriod) {
      localStorage.setItem(benefitPeriodKey, benefitPeriod);
    }
  }

  openDeleteClaimModal = (id, objectId) => {
    this.props.toggleDeleteItemModal(true);
    this.props.setModalClaimId(id);
    this.props.setModalClaimObjectId(objectId);
  };

  closeDeleteClaimModal = () => {
    this.props.toggleDeleteItemModal(false);
    this.props.setModalClaimId(null);
    this.props.setModalClaimObjectId(null);
  };

  onDeleteClaim = claimObjectId => {
    const { deleteClaim, toggleDeleteItemModal, delegateToEmployeeId } = this.props;

    // If delegatedToEmployeeId is myself, return null
    const selectedDelegator =
      delegateToEmployeeId && delegateToEmployeeId.toLowerCase() !== "myself"
        ? delegateToEmployeeId
        : null;

    deleteClaim(claimObjectId, selectedDelegator);
    toggleDeleteItemModal(false);
  };

  render() {
    const {
      benefitPeriod,
      claims,
      claimCategories,
      hasPendingClaims,
      fetchPendingClaimsReport,
      downloadPendingClaimsUrl,
      pageFields,
      intl,
      isDeleteItemModalOpen,
      closeDeleteItemModal,
      modalClaimObjectId,
      modalClaimId,
      isFetching,
      benefitPlans,
      onSubmitSelection,
      claimItems = [],
      employeesByDelegation,
      isEmployeesByDelegationEnabled,
      delegateToEmployeeId,
      disclaimer,
      getDonateCategory,
      ...props
    } = this.props;

    if (this.state.isFetching) {
      return <Loader />;
    }

    const action = { delete: this.openDeleteClaimModal };

    let pending;
    if (hasPendingClaims) {
      pending = claimCategories.map(category => {
        const claimTypeIdArr =
          Array.isArray(claims[category.id]) &&
          claims[category.id].length > 0 &&
          claims[category.id][0].claimTypeId &&
          claims[category.id][0].claimTypeId.split("-");
        const isInsuranceClaim =
          claimTypeIdArr && (claimTypeIdArr[0] === "HS" || claimTypeIdArr[0] === "MO");

        return (
          claims[category.id] &&
          claims[category.id].length > 0 && (
            <div key={category.id}>
              <h3>
                {intl.formatMessage({
                  id: getDonateCategory ? "Donations" : category.label,
                  defaultMessage: getDonateCategory ? "Donations" : category.label,
                })}
              </h3>
              <PendingClaimsTable
                isInsuranceClaim={isInsuranceClaim}
                claims={claims[category.id]}
                columns={pageFields}
                action={action}
                selectedDelegator={delegateToEmployeeId}
                getDonateCategory={getDonateCategory}
              />
              {!isInsuranceClaim && showEstimatedDateMessage(pageFields) && (
                <div
                  className="apl-claims__payment-description"
                  dangerouslySetInnerHTML={{
                    __html:
                      disclaimer ||
                      intl.formatMessage(messages.claims.pendingClaimsEstimatedPaymentDate),
                  }}
                />
              )}
            </div>
          )
        );
      });
    } else if (claimItems.length === 0) {
      pending = <p>{intl.formatMessage(messages.noClaimsDescription)}</p>;
    }
    return (
      <div>
        {isDeleteItemModalOpen && (
          <DeleteClaimDialog
            isOpen
            closeDeleteItemModal={closeDeleteItemModal}
            eventHandler={this.onDeleteClaim}
            claimId={modalClaimId}
            claimObjectId={modalClaimObjectId}
            formLabel={getDonateCategory ? "Donation" : "Claim Form"}
          />
        )}
        <h2>
          {intl.formatMessage(
            getDonateCategory
              ? messages.pendingDonationsDescription
              : messages.pendingClaimsDescription
          )}
        </h2>
        <Card>
          <CardSection>
            {isEmployeesByDelegationEnabled && (
              <Toolbar>
                <ToolbarSection>
                  <DelegatorSelectField
                    formName={formName}
                    employeesByDelegation={employeesByDelegation}
                  />
                </ToolbarSection>
              </Toolbar>
            )}
            <Toolbar>
              <ToolbarSection>
                <PlanYearSelectField benefitPlans={benefitPlans} formName={formName} {...props} />
              </ToolbarSection>
            </Toolbar>
            <p>
              <strong>{intl.formatMessage(messages.claims.noteDescription)}: </strong>
              {intl.formatMessage(
                getDonateCategory
                  ? messages.claims.donationViewDescription
                  : messages.claims.claimViewDescription
              )}{" "}
              <Link to={`/${getDonateCategory ? "donations" : "claims"}/history`}>
                {intl.formatMessage(
                  getDonateCategory
                    ? messages.donationHistoryDescription
                    : messages.claimsHistoryDescription
                )}
              </Link>
              {"."}
            </p>
            {pending}
          </CardSection>
          {downloadPendingClaimsUrl && hasPendingClaims && (
            <Toolbar>
              <ToolbarSection>
                <Button
                  type="button"
                  onClick={() => fetchPendingClaimsReport(downloadPendingClaimsUrl)}
                  secondary
                >
                  <FileDownloadIcon />
                  {intl.formatMessage(messages.exportAllDescription)}
                </Button>
              </ToolbarSection>
            </Toolbar>
          )}
        </Card>
        {isFetching && <Loader />}
        {!isFetching && claimItems.length > 0 && (
          <Card>
            <CardSection>
              <h5>{intl.formatMessage(messages.claims.claimSubmittedStatusDescription)}.</h5>
              <div className="field__label">
                {intl.formatMessage(messages.claims.claimSubmittedStatusNote)}.
              </div>
              <ClaimItemSelectionForm
                onSubmit={onSubmitSelection}
                benefitPlans={benefitPlans}
                functionReferenceId="ClaimList"
                {...this.props}
              />
            </CardSection>
          </Card>
        )}
      </div>
    );
  }
}

PendingClaimsPage.propTypes = {
  claims: PropTypes.object,
  hasPendingClaims: PropTypes.bool,
  params: PropTypes.object,
  loadPendingClaims: PropTypes.func,
  deleteClaim: PropTypes.func,
  fetchPendingClaimsReport: PropTypes.func,
  pageFields: PropTypes.array,
  intl: intlShape.isRequired,
  isFetching: PropTypes.bool,
  deleteClaimStatus: PropTypes.bool,
  claimCategories: PropTypes.array,
  downloadPendingClaimsUrl: PropTypes.string,
  setModalClaimObjectId: PropTypes.func,
  setModalClaimId: PropTypes.func,
  toggleDeleteItemModal: PropTypes.func,
  isDeleteItemModalOpen: PropTypes.bool,
  closeDeleteItemModal: PropTypes.func,
  modalClaimObjectId: PropTypes.number,
  modalClaimId: PropTypes.string,
  loadBenefitPlans: PropTypes.func,
  benefitPeriod: PropTypes.string,
  benefitPlans: PropTypes.array,
  loadClaimItems: PropTypes.func,
  isServiceProviderClaimItem: PropTypes.bool,
  onSubmitSelection: PropTypes.func,
  claimItems: PropTypes.array,
  employeesByDelegation: PropTypes.array,
  isEmployeesByDelegationEnabled: PropTypes.bool,
  getEmployeesByDelegation: PropTypes.func,
  delegateToEmployeeId: PropTypes.string,
  disclaimer: PropTypes.string,
  getDonateCategory: PropTypes.bool,
};

export default enhancePendingClaims(PendingClaimsPage);



// WEBPACK FOOTER //
// ./components/claim/PendingClaimsPage.js